/**
 * Role model — the one-app shell is role-aware (Foundation lock:
 * one portal, role-driven surfaces; NO new top-level navigation).
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  TWO DISTINCT AXES (owner-locked role model, corrected 2026-05-16 —
 *  memory project_avbp_role_model + AVBP_SIGNOFF_AND_PHASE1_PLAN §3 note)
 * ─────────────────────────────────────────────────────────────────────────
 *  1. LOGIN ROLE — who can sign in:
 *       Admin · Staff · Client · HR · Partner · External-Consultant
 *     (HR + Partner LOGIN surfaces are owner-locked LATER sprints — the
 *      role TYPE exists here so gating is correct, but no HR/Partner track
 *      screen is built in step 6/7; they resolve to the placeholder.)
 *
 *  2. PROJECT-PARTICIPANT TAXONOMY — role *within* one project (5 types):
 *       admin · staff · primary_client · client_circle · external_professional
 *     The External-Consultant login role is the sign-in expression of the
 *     External-Professional participant. Client login has three circle
 *     flavours (co-decision · coordination · observer) chosen at add-time —
 *     privacy is per-member.
 *
 *  Step 6 builds three TRACKS keyed off the login role: Admin · Staff ·
 *  Client. HR/Partner/External-Consultant gate correctly but route to the
 *  reserved placeholder (their dedicated sprints are owner-locked later).
 *
 *  Sprint-1 shipped only admin|staff|client + a `staff` stub. Real role
 *  resolution (HR roster → custom claim) is still a later sprint; the
 *  resolver remains a single isolated seam (`resolveRoleStub`).
 * ─────────────────────────────────────────────────────────────────────────
 */

/** LOGIN role — who can sign in (6-role locked set). */
export type Role =
  | 'admin'
  | 'staff'
  | 'client'
  | 'hr'
  | 'partner'
  | 'external_consultant';

/**
 * The three login roles that have a built TRACK in step 6. HR / Partner /
 * External-Consultant are real login roles but their dedicated screens are
 * owner-locked later sprints — they gate correctly and land on the reserved
 * placeholder, never on a half-built surface.
 */
export type TrackRole = 'admin' | 'staff' | 'client';

export const TRACK_ROLES: TrackRole[] = ['admin', 'staff', 'client'];

export function isTrackRole(r: Role | null | undefined): r is TrackRole {
  return r === 'admin' || r === 'staff' || r === 'client';
}

/**
 * PROJECT-PARTICIPANT taxonomy — a participant's role *within* a project
 * (distinct from the login role). 5 owner-locked types.
 */
export type ParticipantType =
  | 'admin'
  | 'staff'
  | 'primary_client'
  | 'client_circle'
  | 'external_professional';

/**
 * Client-circle privacy flavour, chosen at add-time (per-member privacy —
 * a site engineer must never see family/astro). co_decision = full project
 * view (money configurable) · coordination = drawings·stage·remedy·
 * Conversation only, NO family/astro/personal, NO money · observer =
 * read-only journey + delivered report.
 */
export type CircleFlavour = 'co_decision' | 'coordination' | 'observer';

export interface NavItem {
  key: string;
  label: string;
  icon: string;
  path: string;
  /** true → destination is still a reserved placeholder surface. */
  stub?: boolean;
  /**
   * true → an EXTERNAL launcher (HR ↗ / Capture ↗) — opens a sibling app in
   * a new tab via the launcher path, not an in-portal route. The shell
   * renders these with a ↗ affordance and routes them through `launchLegacy`.
   */
  external?: boolean;
  /**
   * Optional live-count badge KEY. The shell resolves the actual number from
   * its live badge map (e.g. unread chats, new leads, queued approvals) and
   * renders the bright-green count pill the spec mandates. Absent → no badge.
   */
  badgeKey?: 'inbox' | 'leads' | 'approvals';
}

/**
 * OPTION-A NAV GROUPING (AVBP_DESIGN_LANGUAGE_AND_NAV_SPEC_v1 §4.1, owner-
 * approved 2026-06-13 / AVBP_DESIGN_NAV_DECISIONS Q7). The 14 flat items
 * become 4 purposeful groups, ordered by how often they are touched. A group
 * with no label (`label: null`) renders its items ungrouped at the top (Home);
 * the `apps` group is pinned to the BOTTOM of the sidebar (spacer above it).
 */
export interface NavGroup {
  /** Group label shown above the items (uppercase caption). null → no label. */
  label: string | null;
  items: NavItem[];
  /** true → this group is pinned to the bottom of the sidebar (Apps). */
  pinBottom?: boolean;
}

/**
 * Per-login-role nav. NO new top-level navigation vs the Foundation lock —
 * every destination is one of the locked rail/tab entries. Step-6 real
 * tracks: Admin · Staff · Client. HR/Partner/External-Consultant reuse the
 * client-shaped minimal nav but every non-conversation destination is the
 * reserved placeholder (their real module is an owner-locked later sprint).
 */
/* ════════════════════ Option-A GROUPED nav (per role) ════════════════════
 *
 * AVBP_DESIGN_LANGUAGE_AND_NAV_SPEC_v1 §4 (Option A, owner-approved 2026-06-13).
 * The desktop sidebar renders these GROUPS with labels + green active state +
 * live count badges. The flat `NAV` map (further below) is DERIVED from these
 * groups so every existing consumer (route-title resolution in AppShell, the
 * staffRoles preview) keeps working unchanged.
 *
 * Naming is the locked set (AVBP_DESIGN_NAV_DECISIONS_2026-06-13):
 *   Team Inbox (mobile "Inbox") · Leads · Projects · Clients · Partners ·
 *   My Tasks · Add-on Services · Forms · Payments · Team Workload ·
 *   Approvals · Trash · Settings · HR ↗ · Capture ↗.
 * The standalone "Conversation" item is DROPPED (Q6 — AI chat is the floating
 * GlobalChatDock; Project Chat opens from a project).
 * ──────────────────────────────────────────────────────────────────────── */

// Shared launcher items (HR ↗ / Capture ↗) — the "Apps" group, team-only,
// pinned to the bottom of the sidebar. `external` routes them through the
// new-tab launcher, not an in-portal route.
const APPS_GROUP: NavGroup = {
  label: 'Apps',
  pinBottom: true,
  items: [
    { key: 'hr', label: 'HR', icon: '◷', path: '/__launch_hr', external: true },
    {
      key: 'capture',
      label: 'Capture',
      icon: '◎',
      path: '/__launch_capture',
      external: true,
    },
  ],
};

export const NAV_GROUPS: Record<Role, NavGroup[]> = {
  admin: [
    { label: null, items: [{ key: 'home', label: 'Home', icon: '⌂', path: '/' }] },
    {
      label: 'Inbox',
      items: [
        // The WhatsApp operator console — RENAMED "Team Inbox" (spec §3; the
        // route path stays /messages so deep links survive). Mobile tab label
        // is the shorter "Inbox".
        { key: 'messages', label: 'Team Inbox', icon: '💬', path: '/messages', badgeKey: 'inbox' },
        { key: 'leads', label: 'Leads', icon: '⌲', path: '/leads', badgeKey: 'leads' },
      ],
    },
    {
      label: 'Work',
      items: [
        // "Projects" is the flat cross-project oversight list. Clients + Partners
        // are now subsumed into "Contacts" (first item in Manage, below).
        // QUOTE MODULE (2026-06-16) — the VISIBLE Quote entry the owner missed.
        { key: 'quotes', label: 'Quotes', icon: '❝', path: '/quotes' },
        { key: 'all-projects', label: 'Projects', icon: '▦', path: '/all-projects' },
        // Consult Door B — host-side management of booked consultations.
        { key: 'consultations', label: 'Consultations', icon: '☎', path: '/consultations' },
        // NAV DECISION (Q8) — "My Tasks" in the nav NOW.
        { key: 'all-tasks', label: 'My Tasks', icon: '☑', path: '/all-tasks' },
        // NAV DECISION (Q3) — "Ancillary" → "Add-on Services".
        { key: 'ancillary', label: 'Add-on Services', icon: '◇', path: '/ancillary' },
        // NAV DECISION (Q4) — "Intake" → "Forms".
        { key: 'intake', label: 'Forms', icon: '✎', path: '/intake' },
      ],
    },
    {
      label: 'Manage',
      items: [
        // MODULE 2 NAV (2026-06-17) — Contacts is the primary entity; replaces
        // the separate Clients + Partners nav entries. Filter tabs on the
        // Contacts screen provide the per-role breakdown.
        { key: 'contacts', label: 'Contacts', icon: '◉', path: '/contacts' },
        // MODULE 2 — Family/Organization Groups (2026-06-17). Admin + staff
        // only; HR / Partner / External-Consultant have no access.
        { key: 'families', label: 'Groups', icon: '◎', path: '/families' },
        // MODULE 4 NAV (2026-06-18) — Properties entity (AD-013). Admin + staff.
        { key: 'properties', label: 'Properties', icon: '⌂', path: '/properties' },
        // NAV DECISION (Q4) — "Money" → "Payments".
        { key: 'money', label: 'Payments', icon: '₹', path: '/money' },
        { key: 'workload', label: 'Team Workload', icon: '◴', path: '/workload' },
        // ORPHAN FIX (spec §0 item 6) — the admin Approvals queue gets a real
        // nav home (was reachable only by typing /admin-queue).
        { key: 'admin-queue', label: 'Approvals', icon: '✓', path: '/admin-queue', badgeKey: 'approvals' },
        { key: 'trash', label: 'Trash', icon: '🗑', path: '/trash' },
        { key: 'settings', label: 'Settings', icon: '⚙', path: '/settings', stub: true },
      ],
    },
    APPS_GROUP,
  ],
  staff: [
    { label: null, items: [{ key: 'home', label: 'Home', icon: '⌂', path: '/' }] },
    {
      label: 'Inbox',
      items: [
        { key: 'messages', label: 'Team Inbox', icon: '💬', path: '/messages', badgeKey: 'inbox' },
        { key: 'leads', label: 'Leads', icon: '⌲', path: '/leads', badgeKey: 'leads' },
      ],
    },
    {
      label: 'Work',
      items: [
        // QUOTE MODULE (2026-06-16) — staff see Quotes too.
        { key: 'quotes', label: 'Quotes', icon: '❝', path: '/quotes' },
        { key: 'all-projects', label: 'Projects', icon: '▦', path: '/all-projects' },
        // Consult Door B — host-side management of booked consultations.
        { key: 'consultations', label: 'Consultations', icon: '☎', path: '/consultations' },
        // Clients + Partners now live under Contacts (Manage group below).
        { key: 'ancillary', label: 'Add-on Services', icon: '◇', path: '/ancillary' },
        { key: 'intake', label: 'Forms', icon: '✎', path: '/intake' },
      ],
    },
    {
      label: 'Manage',
      items: [
        // MODULE 2 NAV (2026-06-17) — Contacts is first-class for staff too.
        { key: 'contacts', label: 'Contacts', icon: '◉', path: '/contacts' },
        // MODULE 2 — Family/Organization Groups (2026-06-17). Staff included.
        { key: 'families', label: 'Groups', icon: '◎', path: '/families' },
        // MODULE 4 NAV (2026-06-18) — Properties entity (AD-013). Staff included.
        { key: 'properties', label: 'Properties', icon: '⌂', path: '/properties' },
        { key: 'settings', label: 'Settings', icon: '⚙', path: '/settings', stub: true },
      ],
    },
    APPS_GROUP,
  ],
  // NOTE — "My Tasks" (/all-tasks) is intentionally ADMIN-ONLY in the nav: the
  // route is admin-gated in App.tsx (cross-team task oversight). The staff
  // "My Tasks" scope is an owner-locked later slice (spec §3); until then a
  // staff My-Tasks nav entry would dead-end on the route guard, so it is
  // omitted here (asserted by tasksStore.test).
  client: [
    {
      label: null,
      items: [
        { key: 'home', label: 'Home', icon: '⌂', path: '/' },
        // NAV DECISION (Q4) — the client sees "My Details" (intake).
        { key: 'intake', label: 'My Details', icon: '✎', path: '/intake' },
        // Client money picture lives on the Journey surface (locked).
        { key: 'journey', label: 'My Journey', icon: '◇', path: '/journey' },
        { key: 'report', label: 'My Reports', icon: '▤', path: '/report' },
        { key: 'settings', label: 'Settings', icon: '⚙', path: '/settings', stub: true },
      ],
    },
  ],
  // HR / Partner / External-Consultant — minimal nav; dedicated screens are
  // owner-locked later sprints (they route to the reserved placeholder).
  hr: [
    {
      label: null,
      items: [
        { key: 'home', label: 'Home', icon: '⌂', path: '/', stub: true },
        { key: 'settings', label: 'Settings', icon: '⚙', path: '/settings', stub: true },
      ],
    },
  ],
  partner: [
    {
      label: null,
      items: [
        { key: 'home', label: 'Home', icon: '⌂', path: '/', stub: true },
        // M3.2 retired the standalone /conversation surface — the thread now
        // lives in the project right panel. Nav link removed.
        { key: 'settings', label: 'Settings', icon: '⚙', path: '/settings', stub: true },
      ],
    },
  ],
  external_consultant: [
    {
      label: null,
      items: [
        { key: 'home', label: 'Home', icon: '⌂', path: '/', stub: true },
        // M3.2 retired the standalone /conversation surface (see above).
        { key: 'settings', label: 'Settings', icon: '⚙', path: '/settings', stub: true },
      ],
    },
  ],
};

/**
 * PURE — flatten a role's grouped nav into the list of IN-PORTAL nav items
 * (external launchers excluded — they are not routes). Used to derive the
 * legacy flat `NAV` map + by the shell's route-title resolver.
 */
export function flatNavItems(groups: NavGroup[]): NavItem[] {
  return groups.flatMap((g) => g.items).filter((i) => !i.external);
}

/**
 * PAGE-TITLE resolver (2026-06-14, Anil — owner feedback: "we should also name
 * the pages… it is really confusing… this navigation is still a problem").
 *
 * Every screen MUST show WHICH page you are on. The old AppShell resolved the
 * title by an EXACT match of `loc.pathname` against the flat nav items and fell
 * back to "Aayatan Veda Portal" — so every nested / dynamic route (rate card,
 * project detail, a client, a partner, staff roles, conversation…) showed the
 * generic brand name, which ALSO duplicated the crumb's brand name. Two fixes:
 * this resolver gives a real title for EVERY route, and the crumb stops
 * repeating the brand (see AppShell `.crumb`).
 *
 * Resolution order:
 *   1. exact top-level nav-item match (per role)  → its label (e.g. "Payments")
 *   2. an explicit SUB-ROUTE / dynamic-route map  → a clear page name
 *   3. fallback                                   → "Portal" (never the doubled
 *                                                    brand name)
 *
 * Pure (no React, no Firebase) so it is unit-testable.
 */
const PAGE_TITLE_MAP: Record<string, string> = {
  '/': 'Home',
  '/design-system': 'Design system',
  '/conversation': 'Conversation',
  '/queue': 'Review queue',
  '/settings/staff-roles': 'Staff roles',
  '/settings/rate-card': 'Rate card',
  '/admin-queue': 'Approvals',
};

/** Dynamic-route prefixes → a clear page title (matched after the exact maps). */
const PAGE_TITLE_PREFIXES: Array<[string, string]> = [
  ['/conversation/', 'Conversation'],
  ['/project/', 'Project'],
  // Module 2 nav: /contacts/:id is the canonical detail; /client/:id is legacy.
  ['/contacts/', 'Contact'],
  ['/client/', 'Contact'],
  ['/partner/', 'Partner'],
  // Module 2 — Groups.
  ['/families', 'Groups'],
  // Module 4 — Properties.
  ['/properties/', 'Property'],
  ['/properties', 'Properties'],
];

/**
 * PURE — resolve a clear, human page title for the current pathname + role.
 * Used by the AppShell header so every screen names itself. Never returns the
 * brand name (so it can never duplicate the crumb).
 */
export function pageTitleFor(
  pathname: string,
  role: Role | null | undefined,
): string {
  const flat = NAV[role ?? 'staff'] ?? NAV.staff;
  // 1. exact top-level nav-item match (role-scoped label, e.g. "Payments").
  const navHit = flat.find((n) => n.path === pathname);
  if (navHit) return navHit.label;
  // 2. explicit sub-route / known-route map.
  if (PAGE_TITLE_MAP[pathname]) return PAGE_TITLE_MAP[pathname];
  // 3. dynamic-route prefix (e.g. /project/<id> → "Project").
  const pref = PAGE_TITLE_PREFIXES.find(([p]) => pathname.startsWith(p));
  if (pref) return pref[1];
  // 4. nested settings/* not otherwise named → "Settings".
  if (pathname.startsWith('/settings/')) return 'Settings';
  return 'Portal';
}

/**
 * Legacy flat per-role nav — DERIVED from `NAV_GROUPS` so there is one source
 * of truth. Existing consumers (route-title lookup, the staffRoles preview)
 * read this unchanged.
 */
export const NAV: Record<Role, NavItem[]> = {
  admin: flatNavItems(NAV_GROUPS.admin),
  staff: flatNavItems(NAV_GROUPS.staff),
  client: flatNavItems(NAV_GROUPS.client),
  hr: flatNavItems(NAV_GROUPS.hr),
  partner: flatNavItems(NAV_GROUPS.partner),
  external_consultant: flatNavItems(NAV_GROUPS.external_consultant),
};

/**
 * MOBILE 5-TAB MODEL (spec §4.2, owner-approved). Exactly 5 fixed tabs —
 * Home · Inbox · Projects · Clients · More — nothing scrolls; everything else
 * lives in the "More" sheet. Per role the front tabs differ slightly: a client
 * has no Team Inbox, so its front tabs are Home · My Details · My Journey ·
 * My Reports · More. The shell builds the "More" sheet from every grouped item
 * NOT already in the front tabs (plus Apps + account).
 *
 * Q5 (owner) asked for a user-CONFIGURABLE tab bar with a per-role DEFAULT —
 * this is the per-role DEFAULT; the per-user override is a later slice (the
 * shell already reads this through `mobilePrimaryKeys` so the override drops in
 * at one seam).
 */
export const MOBILE_PRIMARY_KEYS: Record<Role, string[]> = {
  // Module 2 nav (2026-06-17) — contacts replaces clients as the 4th front tab.
  admin: ['home', 'messages', 'all-projects', 'contacts'],
  staff: ['home', 'messages', 'all-projects', 'contacts'],
  client: ['home', 'intake', 'journey', 'report'],
  hr: ['home'],
  partner: ['home', 'queue'],
  external_consultant: ['home', 'queue'],
};

/** Mobile tab labels are shorter than the sidebar labels for the 5-tab bar. */
export const MOBILE_TAB_LABEL: Record<string, string> = {
  messages: 'Inbox',
  'all-projects': 'Projects',
  intake: 'Details',
  journey: 'Journey',
  report: 'Reports',
};

/**
 * PURE — resolve the front mobile-tab keys for a role (the per-user override
 * seam: today it returns the role default; a stored preference will be read
 * here later). Always at most 4 keys (the 5th front tab is the fixed "More").
 */
export function mobilePrimaryKeys(role: Role | null | undefined): string[] {
  return MOBILE_PRIMARY_KEYS[role ?? 'staff'] ?? MOBILE_PRIMARY_KEYS.staff;
}

/**
 * Surface-visibility contract derived from the login role. This is THE
 * single predicate set the track screens + the conversation thread consult
 * so the owner-locked visibility split is enforced in one place, not
 * sprinkled across components.
 *
 * THE SPINE (owner-locked 2026-05-17): the client track NEVER renders
 * money internals, the AI/review layer, or internal notes. The shared
 * conversation thread shows the client their own messages + team replies +
 * milestone events (quote generated · report sent · payment made) only.
 */
export interface RoleVisibility {
  /** Sees money internals (payments overview, gate amounts, ledger money). */
  seesMoneyInternals: boolean;
  /** Sees the AI extraction / drafts / Review Queue layer. */
  seesReviewLayer: boolean;
  /** Sees internal team notes layered on the conversation. */
  seesInternalNotes: boolean;
  /** Sees the cross-project aggregate (admin only). */
  seesCrossProject: boolean;
  /** Can COMMIT a proposal / clear a gate (assigned staff OR admin). */
  canCommit: boolean;
}

export function visibilityFor(role: Role | null | undefined): RoleVisibility {
  switch (role) {
    case 'admin':
      return {
        seesMoneyInternals: true,
        seesReviewLayer: true,
        seesInternalNotes: true,
        seesCrossProject: true,
        canCommit: true,
      };
    case 'staff':
      return {
        seesMoneyInternals: true,
        seesReviewLayer: true,
        seesInternalNotes: true,
        seesCrossProject: false,
        canCommit: true,
      };
    case 'client':
    default:
      // Client + every non-track role default to the SAFE floor: no money
      // internals, no AI/review layer, no internal notes, no cross-project,
      // no commit rights. (HR/Partner/External-Consultant land on the
      // placeholder anyway — this is the conservative default.)
      return {
        seesMoneyInternals: false,
        seesReviewLayer: false,
        seesInternalNotes: false,
        seesCrossProject: false,
        canCommit: false,
      };
  }
}

/**
 * Commit-rights resolver (owner-locked: commit rights = the project's
 * assigned staff OR Admin — memory project_curation_gating_assignee_model,
 * no tier-fallback). The client-side check is UX-only; the authoritative
 * recheck is server-side (see avbp-functions recheckGate / firestore.rules
 * description in the step-6 server-recheck deliverable).
 */
export function canCommitToProject(
  role: Role | null | undefined,
  signedInUid: string | null | undefined,
  projectAssigneeUid: string | null | undefined,
): boolean {
  if (role === 'admin') return true;
  if (role === 'staff' && signedInUid && projectAssigneeUid) {
    return signedInUid === projectAssigneeUid;
  }
  return false;
}

/**
 * Sprint-1 role stub. Until the HR-roster → custom-claim pipeline lands
 * (later sprint), a signed-in user is treated as `staff`. Single isolated
 * seam — the real resolver drops in here with no shell changes.
 *
 * DEMO: a `?role=` query param OR a persisted localStorage choice lets a
 * reviewer walk all three tracks from one sign-in without a roster. This
 * is a review affordance ONLY (clearly not production identity) and is the
 * single place the override lives.
 */
const ROLE_KEY = 'avbp.demoRole';

export function resolveRoleStub(): Role {
  if (typeof window !== 'undefined') {
    const q = new URLSearchParams(window.location.search).get('role');
    const fromQuery = normaliseRole(q);
    if (fromQuery) {
      try {
        window.localStorage.setItem(ROLE_KEY, fromQuery);
      } catch {
        /* private mode — query param still wins for this load */
      }
      return fromQuery;
    }
    try {
      const stored = normaliseRole(window.localStorage.getItem(ROLE_KEY));
      if (stored) return stored;
    } catch {
      /* ignore */
    }
  }
  return 'staff';
}

/**
 * Round 3 (2026-05-18) — FIX 1 (#914): derive the LOGIN role from the real
 * Firebase custom claims minted by `resolveTeamClaimsOnSignIn` (locked auth
 * model: the role claim drives the UI). Pure — no Firebase import — so it is
 * unit-testable here alongside the rest of the role logic.
 *
 * Claim shape (mirrors avbp_auth_v2 + avbp_auth_oauth):
 *   { isTeam:true, roles:string[], role:'admin'|'staff'|..., ... }  → team
 *   (no isTeam / no roles)                                          → client
 *
 * `roles[]` is the authoritative list; legacy additive `role` is the
 * fallback. `admin` wins over `staff`. A signed-in user with NO team claim
 * is a client (correct — clients sign in with the same Google/Apple flow
 * but are never in the staff master).
 */
export function roleFromClaims(
  claims: Record<string, unknown> | null | undefined,
): Role {
  if (!claims) return 'client';
  const isTeam = claims.isTeam === true;
  const rolesArr = Array.isArray(claims.roles)
    ? (claims.roles as unknown[]).map((r) => String(r).toLowerCase())
    : [];
  const legacy =
    typeof claims.role === 'string' ? claims.role.toLowerCase() : '';

  if (!isTeam && rolesArr.length === 0 && !legacy) return 'client';

  if (rolesArr.includes('admin') || legacy === 'admin') return 'admin';
  // Any team member who is not admin renders on the staff track (HR /
  // Partner / External-Consultant dedicated tracks are owner-locked later
  // sprints; their role TYPE still resolves correctly here).
  const candidate = normaliseRole(rolesArr[0] || legacy);
  if (candidate) return candidate;
  return isTeam ? 'staff' : 'client';
}

/** Persist a demo-track choice (used by the in-shell role switcher). */
export function setDemoRole(role: Role): void {
  try {
    window.localStorage.setItem(ROLE_KEY, role);
  } catch {
    /* ignore */
  }
}

function normaliseRole(raw: string | null | undefined): Role | null {
  if (!raw) return null;
  const v = raw.toLowerCase().replace(/[\s-]+/g, '_');
  const all: Role[] = [
    'admin',
    'staff',
    'client',
    'hr',
    'partner',
    'external_consultant',
  ];
  return (all as string[]).includes(v) ? (v as Role) : null;
}
